import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Lock, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [token, setToken] = useState('');

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      toast({
        title: 'Error',
        description: 'Invalid reset link',
        variant: 'destructive'
      });
      navigate('/signin');
    } else {
      setToken(resetToken);
    }
  }, [searchParams, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (formData.new_password.length < 8) {
      toast({
        title: 'Error',
        description: 'Password must be at least 8 characters long',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.new_password
      });
      
      setResetSuccess(true);
      toast({
        title: 'Success',
        description: 'Your password has been reset successfully!',
      });
      
      // Redirect to signin after 3 seconds
      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    } catch (error) {
      let errorMessage = 'Failed to reset password. Link may be expired.';
      
      // Check if this is a network error (offline or backend unreachable)
      if (!error.response) {
        errorMessage = 'Cannot connect to server. Please check your internet connection and try again.';
      } else {
        // Use the backend's error message if available
        errorMessage = error.response?.data?.detail || error.response?.data?.message || errorMessage;
        if (typeof errorMessage === 'string' && /reset link|reset token/i.test(errorMessage)) {
          setTokenError(true);
        }
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-2">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
              resetSuccess 
                ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                : 'bg-gradient-to-br from-blue-500 to-green-500'
            }`}>
              {resetSuccess ? (
                <CheckCircle2 className="w-10 h-10 text-white" />
              ) : (
                <Lock className="w-10 h-10 text-white" />
              )}
            </div>
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-white">
              {resetSuccess ? 'Password Reset!' : 'Reset Your Password'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {resetSuccess 
                ? 'Redirecting to sign in...'
                : 'Enter your new password below'
              }
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          {!resetSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={formData.new_password}
                    onChange={handleChange}
                    required
                    className="h-11 pr-10"
                    minLength={8}
                    data-testid="reset-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-500">At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold transition-all"
                disabled={isLoading}
                data-testid="reset-password-submit-btn"
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
              {tokenError && (
                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg text-center space-y-2" data-testid="reset-token-error-box">
                  <p className="text-sm text-amber-800">
                    This link is no longer valid — it may have already been used or expired. Request a fresh one below.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-10 border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => navigate('/forgot-password')}
                    data-testid="request-new-reset-link-btn"
                  >
                    Request a new reset link
                  </Button>
                </div>
              )}
            </form>
          ) : (
            <div className="space-y-4">
              <div className="p-5 bg-green-50 border-2 border-green-200 rounded-lg text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-600 mb-3" />
                <p className="text-lg font-semibold text-green-800 mb-2">
                  Password Successfully Reset!
                </p>
                <p className="text-sm text-green-700">
                  You can now sign in with your new password.
                </p>
              </div>
              <Button
                onClick={() => navigate('/signin')}
                className="w-full h-11 bg-[#1E88E5] hover:bg-blue-700"
              >
                Go to Sign In
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;